/**
 * The gameplay screen (docs/GAME_DESIGN.md § 1, 6, 8): the well with its
 * falling sums, the HUD and the answer panel, over the animated theme scene.
 *
 * Everything that happens comes from the live GameState (useGameEngine); this
 * component only composes the pieces in game/ and wires them together:
 * - Layouts, all in game.css: portrait (HUD, well, answers stacked), landscape
 *   (well beside a column with the HUD and the answers, mirrored for
 *   left-handed players) and the compact Watch layout (stack meter + big sum
 *   + 2×2 answers).
 * - The answer mode (choices or handwriting) follows the settings, the device
 *   and the in-game toggle (inputMode.ts). Every mode submits through the same
 *   engine call; there is exactly one handwriting pad.
 * - Pausing (button, Escape, the tab hiding) covers the whole game, so the sum
 *   is hidden, and makes everything behind the pause card inert.
 * - A polite live region says each new sum and each outcome.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GameState, Locale } from '@dropstack/core';
import type { GameScreenProps, HwResult } from '../contracts.ts';
import { AdSlot } from '../components/AdSlot.tsx';
import { useT } from '../i18n/index.ts';
import { BackgroundCanvas } from '../theme/index.ts';
import { AnswerPanel, isForeignKey } from '../game/AnswerPanel.tsx';
import { INITIAL_ANNOUNCER_MEMO, takeAnnouncements, themeForLevel, type AnnouncerMemo } from '../game/announce.ts';
import { CompactPlayfield } from '../game/CompactPlayfield.tsx';
import { Hud, useTickingScore } from '../game/Hud.tsx';
import { useGameTheme, useMediaQuery, usePenUsed } from '../game/hooks.ts';
import {
  chooseInputMode,
  effectiveInputMode,
  fallBackToChoices,
  INITIAL_INPUT_MODE_STATE,
  resolveInputMode,
  type InputMode,
} from '../game/inputMode.ts';
import { PauseOverlay } from '../game/PauseOverlay.tsx';
import { Playfield } from '../game/Playfield.tsx';
import { displayLevel, sessionAccuracy, stackIntensity } from '../game/playfield.ts';
import { useGameEngine } from '../game/useGameEngine.ts';
import '../game/game.css';

/**
 * The optional landscape side banner (docs/MONETIZATION.md). Off by default:
 * flip only after a product decision. Ads never appear inside the playfield.
 */
const PLAY_SIDE_BANNER = false;

/** "Larger text": how much bigger the maths is drawn (the rest of the UI scales with the root font size). */
const LARGE_TEXT_SCALE = 1.15;

/**
 * A live region only speaks changes made after it exists (and the screen
 * reader has noticed it): nothing is said until it has been on screen this long.
 */
const LIVE_REGION_READY_MS = 250;

/**
 * The live region: announcements for each state change (announce.ts),
 * written straight into the element so speaking never re-renders the game.
 *
 * The engine produces a new state every frame, so what gets said is tracked
 * by what was actually spoken (AnnouncerMemo), never by the previous state:
 * the first sum, held back until the region is ready, is still due when the
 * ready timer fires however many frames have passed.
 */
function useAnnouncer(state: GameState, locale: Locale) {
  const regionRef = useRef<HTMLParagraphElement>(null);
  const memoRef = useRef<AnnouncerMemo>(INITIAL_ANNOUNCER_MEMO);
  const latestRef = useRef({ state, locale });
  const readyRef = useRef(false);

  const speak = useCallback(() => {
    const region = regionRef.current;
    if (!readyRef.current || !region) return;
    const { state: s, locale: l } = latestRef.current;
    const { messages, memo } = takeAnnouncements(memoRef.current, s, l);
    memoRef.current = memo;
    if (messages.length) region.textContent = messages.join(' ');
  }, []);

  // Mount only (and StrictMode's remount): the timer is never cancelled by a new frame.
  useEffect(() => {
    const timer = setTimeout(() => {
      readyRef.current = true;
      speak();
    }, LIVE_REGION_READY_MS);
    return () => {
      clearTimeout(timer);
      readyRef.current = false;
    };
  }, [speak]);

  useEffect(() => {
    latestRef.current = { state, locale };
    speak();
  }, [state, locale, speak]);

  return regionRef;
}

export function GameScreen(props: GameScreenProps) {
  const { settings, ticket, audio, recognizer, ads, compact, onGameOver, onOpenSettings } = props;
  const t = useT();
  const { locale } = settings;
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const reducedMotion = settings.reducedMotion || prefersReducedMotion;
  const textScale = settings.largeText ? LARGE_TEXT_SCALE : 1;

  const { state, now, paused, submit, pause, resume, quit } = useGameEngine({ ticket, audio, onGameOver });
  const over = state.phase === 'gameOver';
  const canPause = !paused && !over;

  // The level on show and its theme (page colours, background scene, music).
  const level = displayLevel(state);
  const theme = useMemo(() => themeForLevel(level), [level]);
  useGameTheme(theme, audio);

  const score = useTickingScore(state.raw, now, reducedMotion);
  const accuracy = sessionAccuracy(state);

  // ───────────── Answer input ─────────────

  const coarsePointer = useMediaQuery('(pointer: coarse)');
  const penUsed = usePenUsed();
  const baseMode = resolveInputMode(settings.input, { coarsePointer, penUsed }, settings.handwritingCalibrated);
  const [modeState, setModeState] = useState(INITIAL_INPUT_MODE_STATE);
  // The falling block's problem — or the one just resolved, shown with feedback until the next spawns.
  const answerProblem = state.active?.problem ?? state.lastResolution?.problem ?? null;
  const answerProblemId = answerProblem?.id ?? null;
  const mode = effectiveInputMode(baseMode, modeState, answerProblemId);
  const inputDisabled = state.phase !== 'falling' || paused;

  const onChooseMode = useCallback((next: InputMode) => setModeState(chooseInputMode(next)), []);
  const onFallback = useCallback(() => {
    if (answerProblemId !== null) setModeState((s) => fallBackToChoices(s, answerProblemId));
  }, [answerProblemId]);
  const onRecognized = useCallback(
    (result: HwResult) => {
      if (result.text) audio.play('hwRecognized');
    },
    [audio],
  );

  // ───────────── Pause ─────────────

  const rootRef = useRef<HTMLElement>(null);
  const onResume = useCallback(() => {
    resume();
    // The pause card is going away: keep keyboard focus inside the game.
    rootRef.current?.focus({ preventScroll: true });
  }, [resume]);

  // Escape (or P) pauses; the pause card handles its own keys.
  useEffect(() => {
    if (!canPause) return;
    const onKey = (e: KeyboardEvent) => {
      if (isForeignKey(e) || (e.key !== 'Escape' && e.key !== 'p' && e.key !== 'P')) return;
      e.preventDefault();
      pause();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canPause, pause]);

  const liveRef = useAnnouncer(state, locale);

  // ───────────── Layout ─────────────

  const landscape = useMediaQuery('(orientation: landscape)');
  const showSideBanner = PLAY_SIDE_BANNER && !compact && landscape && ads.enabled;

  const classes = [
    'game',
    compact ? 'game--compact' : 'game--full',
    settings.leftHanded && 'game--left-handed',
    reducedMotion && 'game--reduced-motion',
    over && 'game--over',
  ]
    .filter(Boolean)
    .join(' ');

  const answers = (
    <AnswerPanel
      problem={answerProblem}
      resolution={state.lastResolution}
      mode={mode}
      disabled={inputDisabled}
      locale={locale}
      recognizer={recognizer}
      compact={compact}
      onAnswer={submit}
      onFallback={onFallback}
      onChooseMode={onChooseMode}
      onRecognized={onRecognized}
    />
  );

  return (
    <section ref={rootRef} className={classes} aria-label={t('game.screenLabel')} tabIndex={-1}>
      {!compact && (
        <div className="game__backdrop" aria-hidden="true">
          <BackgroundCanvas theme={theme} reducedMotion={reducedMotion} paused={paused} intensity={stackIntensity(state)} />
        </div>
      )}

      <div className="game__stage" inert={paused}>
        {compact ? (
          <div className="game__field">
            <CompactPlayfield
              state={state}
              now={now}
              locale={locale}
              score={score}
              level={level}
              onPause={pause}
              canPause={canPause}
              mode={mode}
              onChooseMode={onChooseMode}
            />
          </div>
        ) : (
          <>
            <div className="game__hud">
              <Hud
                score={score}
                level={level}
                themeName={theme.name[locale]}
                accuracy={accuracy}
                next={state.next}
                locale={locale}
                onPause={pause}
                canPause={canPause}
              />
            </div>
            <div className="game__well">
              <Playfield state={state} now={now} theme={theme} locale={locale} reducedMotion={reducedMotion} textScale={textScale} />
            </div>
          </>
        )}
        <div className={`game__answers game__answers--${mode}`}>{answers}</div>
        {showSideBanner && (
          <aside className="game__ad">
            <AdSlot ads={ads} slot="play-side-banner" enabled />
          </aside>
        )}
      </div>

      <p ref={liveRef} className="visually-hidden" aria-live="polite" aria-atomic="true" />

      {paused && <PauseOverlay onResume={onResume} onQuit={quit} onOpenSettings={onOpenSettings} compact={compact} />}
    </section>
  );
}
